import debug from 'debug'
import type { SetStateAction } from 'react'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { parseQuery } from 'ufo'

const log = debug('pixai:qs')

function getDefaultSearchParams() {
  if (typeof window === 'undefined') {
    return new URLSearchParams()
  }
  return new URL(window.location.href).searchParams
}

export const getQs = (
  searchParams: URLSearchParams = getDefaultSearchParams(),
) => {
  const qs = Object.fromEntries([...searchParams.entries()])
  return qs
}

export const fromQs = (obj: Record<string, any>, init?: URLSearchParams) => {
  const searchParams = new URLSearchParams(init)
  Object.entries(obj).forEach(([k, v]) => {
    if (v === null) {
      searchParams.delete(k)
    } else if (v) {
      searchParams.set(k, v)
    }
  })
  return searchParams
}
export const formatQs = (obj: Record<string, any>, init?: URLSearchParams) => {
  return fromQs(obj, init).toString()
}

type Qs = Record<string, string>

export const useQs = () => {
  const [params, setParams] = useSearchParams()

  const qsData = useMemo(() => {
    return getQs(params) as Qs
  }, [params])

  /**
   * setParams's prev value can be stale
   * Simulates a setState-like callback function
   * @see https://github.com/remix-run/react-router/issues/9757
   */
  const setQsData = useCallback(
    (action: SetStateAction<Record<string, any>>) => {
      // Get "prev" value from window.location.search directly
      const oldValue = parseQuery(window.location.search)
      const data = typeof action === 'function' ? action(oldValue) : action
      const newParams = fromQs(data)

      log('setQs', oldValue, data)

      // Preserve hash when updating search params
      const currentHash = window.location.hash

      // Not using its prev value
      setParams(newParams, { replace: true, preventScrollReset: true })

      // Restore hash after setParams (async) completes
      if (currentHash) {
        requestAnimationFrame(() => {
          if (window.location.hash !== currentHash) {
            window.history.replaceState(
              null,
              '',
              window.location.pathname + window.location.search + currentHash,
            )
          }
        })
      }
    },
    [setParams],
  )

  return [qsData, setQsData] as const
}

export const formatParamURL = (
  base: string,
  params: Record<string, string | undefined>,
) => {
  const url = new URL(base)
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value)
    }
  })
  return url.toString()
}
